import { cx } from '@linaria/core';
import { useState, useRef, useEffect, useCallback } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { useNavigationManager } from '@provider/navigation';
import { useSpeechSynthesisConfig } from '@provider/speech-synthesis';
import { useUserIdentityState } from '@provider/user-identity';
import { breakpoints } from '@ui/css';
import { scrollTo, asyncTimeout, describeArcPath } from '@utils/common';
import { makeSelectors } from '@utils/css';
import { useSelectionRange, useMatchBreakpoint, useIsomorphicLayoutEffect, CSSRefTransition } from '@utils/react';
import { FlagButton } from './flag-button.js';
import { buttonCls } from './flag-button.style.js';
import { usePolly } from './hook.polly.js';
import { progressCls, loaderCls, iconCls, karaokeHighlightCls, KARAOKE_TRANSITION_DURATION_MS, buttonWrapperCls, BUTTON_FADE_DURATION_MS, buttonCls as buttonCls$1, PROGRESS_STROKE_WIDTH_PX, guideBubbleCls, flagButtonsCls, BUTTON_MOBILE_SIZE_PX, BUTTON_SIZE_PX } from './speech-button.style.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { selectNextContentElement, speechButtonSelectors } from './utils/selectNextContentElement.js';
import { createRangeFromNodeContent } from './utils/createRangeFromNodeContent.js';
import { FloatingActionButton } from '../buttons/fab.js';
import { ReadOutLoudIcon } from '../../icons/read-out-loud.js';
import { PauseIcon } from '../../icons/pause.js';
import { GuideBubble } from '../atoms/guide-bubble.js';

const speechSelectors = makeSelectors({
  enableSpeech: "polly--enabled",
  progress: progressCls,
  loader: loaderCls,
  icon: iconCls,
  flagButton: buttonCls
});
function SpeechButton({
  wrapperClassName,
  flagPlacement,
  className,
  rootSelector = speechSelectors.enableSpeech(),
  scrollContainerRef,
  disableSelection,
  defaultTooltip,
  hideWhenEmpty,
  hidePreSelection,
  hide,
  tooltipPlacement,
  withoutGuideBubble,
  forceVisibleLangaugeSelector
}) {
  const config = useSpeechSynthesisConfig();

  // Polly should only be available for authenticated users, see ADO #114620
  const isLoggedIn = useUserIdentityState(state => !!state.user);
  const navigation = useNavigationManager();
  const selectionRange = useSelectionRange(rootSelector);
  const selection = !disableSelection ? selectionRange : undefined;
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  const [hideBecauseEmpty, setHideBecauseEmpty] = useState(false);
  const [hideBecauseNoSelection, setHideBecauseNoSelection] = useState(false);
  const [showTooltips, setShowTooltips] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const tooltipTimeoutRef = useRef();
  const [language, setLanguage] = useState(config.languages?.[0] ?? "Danish");
  const {
    isUnavailable,
    isReading,
    isPlaying,
    isLoading,
    showLoader,
    progress,
    startPolly,
    pausePolly,
    resumePolly,
    stopPolly,
    changePollyLanguage
  } = usePolly({
    enabled: config.enabled,
    speed: config.speed,
    karaoke: config.karaoke,
    gender: config.gender,
    latexPrinter: config.latexPrinter,
    initialLanguage: config.languages?.[0],
    highlightCls: karaokeHighlightCls,
    highlightTransitionMs: KARAOKE_TRANSITION_DURATION_MS,
    scrollContainerRef,
    rootSelector
  });

  // auto-stop polly when user changes the selection or navigates to a new
  // page
  useEffect(() => {
    return navigation.subscribe(() => stopPolly());
  }, [stopPolly, navigation]);
  useEffect(() => {
    if (selection !== undefined) {
      stopPolly();
    }
  }, [stopPolly, selection]);

  // auto-remove selection when playback begins
  useEffect(() => {
    if (isReading) {
      window.getSelection()?.removeAllRanges();
    }
  }, [isReading]);

  // auto-stop polly if user clicks any link or buttons while his text is
  // being read out loud (we must assume he's changed his focus)
  useEffect(() => {
    if (!isReading) {
      return;
    }
    const handleClick = evt => {
      if (!evt.target || !(evt.target instanceof HTMLElement)) {
        return;
      }

      // NB: any elements that the polly hook may interact with
      // programatically should not stop playback.
      if (evt.target.classList.contains(speechButtonSelectors.accordionToggleButton.className) || evt.target.classList.contains(speechButtonSelectors.textComponentToggleButton.className) || evt.target.classList.contains(speechButtonSelectors.attachedAudioButton.className) || evt.target.closest(speechButtonSelectors.attachedAudioPlayer())) {
        return;
      }
      const stopElements = ["a", "button"];
      if (stopElements.includes(evt.target.tagName.toLowerCase()) || evt.target.closest(stopElements.join(", "))) {
        if (evt.target.hasAttribute("data-dismiss")) {
          // if this is a dismiss button, then allow Polly to continue running
          return;
        }
        stopPolly();
      }
    };
    window.addEventListener("click", handleClick);
    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, [isReading, stopPolly]);

  // deferred showing of tooltips when user is reading out loud if language
  // selection is available, or if the user has made a selection
  useEffect(() => {
    const hasSelection = selection !== undefined;
    window.clearTimeout(tooltipTimeoutRef.current);
    if (hasSelection && !showTooltips) {
      tooltipTimeoutRef.current = window.setTimeout(() => {
        setShowTooltips(true);
      }, 200);
    } else if (!hasSelection && showTooltips) {
      setShowTooltips(false);
    }
  }, [selection, showTooltips]);

  // auto-hide button if there's no selectable text in the root selector
  useIsomorphicLayoutEffect(() => {
    if (!hideWhenEmpty) {
      setHideBecauseEmpty(false);
      return;
    }
    const rootElement = document.querySelector(rootSelector);
    if (!rootElement) {
      setHideBecauseEmpty(true);
      return;
    }
    const rootRange = document.createRange();
    rootRange.selectNodeContents(rootElement);
    if (rootRange.toString().trim().length > 0) {
      setHideBecauseEmpty(false);
      return;
    }

    // if we get here, there's no (immediately available) content for the user
    // to read out loud - hide the button, but observe for content to be added
    // to the lightbox
    setHideBecauseEmpty(true);
    const domObserver = new MutationObserver(() => {
      rootRange.selectNodeContents(rootElement);
      if (rootRange.toString().trim().length > 0) {
        setHideBecauseEmpty(false);

        // we no longer need to observe for changes if we've shown the button
        // at some point
        domObserver.disconnect();
      }
    });
    domObserver.observe(rootElement, {
      subtree: true,
      childList: true
    });
  }, [hideWhenEmpty, rootSelector]);

  // auto-hide button pre selection if enabled
  useIsomorphicLayoutEffect(() => {
    if (hidePreSelection) {
      setHideBecauseNoSelection(selection === undefined);
    }
  }, [hidePreSelection, selection]);
  const handleClick = useCallback(async evt => {
    evt.stopPropagation();
    if (isLoading) {
      // avoid doing further handling while we're loading speech synthesis,
      // as it can mess with state management of the underlying audio controls
      //
      // see ADO #111433
      return;
    }
    if (isReading) {
      if (isPlaying) {
        pausePolly();
      } else {
        resumePolly();
      }
      return;
    }
    setHasUserInteracted(true);
    if (selection !== undefined) {
      // if the user made a selection, then begin reading that range out loud
      // and remove the actual selection visually
      startPolly(selection, language);
      return;
    }

    // if reading the entire page, then initially scroll the user all the way
    // to the top of the page
    if (config.karaoke && rootSelector === speechSelectors.enableSpeech.className) {
      scrollTo(0, {
        scrollContainer: scrollContainerRef?.current ?? undefined
      });
    }

    // and then begin reading each range one by one
    let currentElement;
    startPolly(async () => {
      currentElement = await selectNextContentElement(rootSelector, currentElement);
      const attachedAudioButton = currentElement?.querySelector(speechButtonSelectors.attachedAudioButton());

      // NB: In case the current content element contains an attached audio
      // player, then we should prefer this audio over polly playback.
      if (attachedAudioButton) {
        attachedAudioButton.click();
        await asyncTimeout(50);
        const playingAudioElement = [...document.querySelectorAll("audio")].find(it => it.readyState > 2 ? !it.paused : it.autoplay);
        if (playingAudioElement) {
          return playingAudioElement;
        }
      }
      return createRangeFromNodeContent(currentElement);
    }, language);
    if (selection !== undefined) {
      // remove selection if we're reading the selected text out loud
      window.getSelection()?.removeAllRanges();
    }
  }, [isLoading, isPlaying, isReading, config.karaoke, language, pausePolly, resumePolly, rootSelector, scrollContainerRef, selection, startPolly]);
  const handleLanguageClick = language => {
    // update state right away so we'll start next reading in this language
    setLanguage(language);

    // ... and if we're currently reading out loud, then restart the progress
    // with the new language
    changePollyLanguage(language);
  };

  // bind a global keyboard listener, which will trigger synthetic speech when
  // the user applies the global speech shortcut
  useEffect(() => {
    const handleKeyDown = evt => {
      if (evt.shiftKey && evt.ctrlKey && !evt.altKey && !evt.metaKey && evt.key === "S") {
        handleClick(evt);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleClick]);
  if (!isLoggedIn || isUnavailable || hideBecauseEmpty) {
    return null;
  }
  const label = isReading ? isPlaying ? "Pause" : "Fortsæt oplæsning (Shift + Ctrl + S)" : selection !== undefined ? "Læs op (Shift + Ctrl + S)" : defaultTooltip ?? "Læs siden op (Shift + Ctrl + S)";
  const buttonSizePx = isMobile ? BUTTON_MOBILE_SIZE_PX : BUTTON_SIZE_PX;
  const shouldHidePreSelection = hideBecauseNoSelection && !isReading;
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(CSSRefTransition, {
      in: !hide && !shouldHidePreSelection,
      classNames: buttonWrapperCls,
      timeout: BUTTON_FADE_DURATION_MS,
      mountOnEnter: true,
      unmountOnExit: true,
      children: ref => /*#__PURE__*/jsxs("div", {
        ref: ref,
        className: cx(buttonWrapperCls, wrapperClassName),
        children: [/*#__PURE__*/jsxs(FloatingActionButton, {
          className: cx(className, buttonCls$1, (isPlaying || isReading) && "active"),
          onClick: handleClick,
          tooltip: {
            placement: tooltipPlacement ?? "left",
            body: label,
            ariaType: "label"
          },
          children: [/*#__PURE__*/jsx(FloatingActionButton.Icon, {
            contentKey: "readOutLoud",
            active: !isPlaying,
            children: /*#__PURE__*/jsx(ReadOutLoudIcon, {
              className: iconCls,
              playOnly: selection !== undefined || isReading
            })
          }), /*#__PURE__*/jsx(FloatingActionButton.Icon, {
            contentKey: "pause",
            active: isPlaying,
            children: /*#__PURE__*/jsx(PauseIcon, {
              className: cx(iconCls, "pause")
            })
          }), /*#__PURE__*/jsx(FloatingActionButton.Icon, {
            contentKey: "loader",
            active: showLoader,
            disableScaleDuringTransition: true,
            children: /*#__PURE__*/jsx("span", {
              className: loaderCls
            })
          }), /*#__PURE__*/jsx(FloatingActionButton.Icon, {
            contentKey: "progress",
            active: isReading && !showLoader,
            disableScaleDuringTransition: true,
            children: /*#__PURE__*/jsxs("svg", {
              xmlns: "http://www.w3.org/2000/svg",
              viewBox: `0 0 ${buttonSizePx} ${buttonSizePx}`,
              className: progressCls,
              children: [/*#__PURE__*/jsx("path", {
                className: "track",
                d: describeArcPath(buttonSizePx, PROGRESS_STROKE_WIDTH_PX, 359.99)
              }), progress > 0 && /*#__PURE__*/jsx("path", {
                d: describeArcPath(buttonSizePx, PROGRESS_STROKE_WIDTH_PX, progress * 360)
              }, "progress")]
            })
          })]
        }), !withoutGuideBubble && /*#__PURE__*/jsx(GuideBubble, {
          id: "speechSynthesis",
          className: guideBubbleCls,
          popperPlacement: "left",
          label: "Læs om hvordan oplæsning af tekst fungerer",
          title: "Oplæsning af tekst",
          hasUserInteracted: hasUserInteracted,
          children: "Klik p\xE5 knappen og f\xE5 l\xE6st hele siden op. Du har ogs\xE5 mulighed for at f\xE5 l\xE6st et kortere tekststykke op, ved at markere teksten og derefter klikke p\xE5 knappen. Du kan \xE6ndre p\xE5 opl\xE6sningshastigheden i indstillinger i dit profilomr\xE5de."
        })]
      })
    }), /*#__PURE__*/jsx(TransitionGroup, {
      comopnent: null,
      children: !hide && config.languages?.[0] && config.languages[1] && (selection !== undefined || isReading || forceVisibleLangaugeSelector) && /*#__PURE__*/jsx(CSSRefTransition, {
        timeout: 150,
        children: ref => /*#__PURE__*/jsx("div", {
          ref: ref,
          className: flagButtonsCls,
          "data-placement": flagPlacement ?? "bottom",
          children: config.languages?.[0] && config.languages[1] && /*#__PURE__*/jsxs(Fragment, {
            children: [/*#__PURE__*/jsx(FlagButton, {
              tooltipPlacement: flagPlacement === "left" ? "top" : "left",
              language: config.languages[0],
              active: language === config.languages[0],
              showTooltip: showTooltips,
              onClick: handleLanguageClick
            }), /*#__PURE__*/jsx(FlagButton, {
              tooltipPlacement: flagPlacement === "left" ? "top" : "left",
              language: config.languages[1],
              active: language === config.languages[1],
              showTooltip: showTooltips,
              onClick: handleLanguageClick
            })]
          })
        })
      })
    })]
  });
}

export { SpeechButton, speechSelectors };
//# sourceMappingURL=speech-button.js.map
